import $ from '@sparta.global.vendors/jquery';
import '@sparta.global.vendors/lodash';
import spartaBootstrapUtility from '@sparta.global.utilities/sparta-bootstrap-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaExperienceUtility from '@sparta.global.utilities/sparta-experience-utility/dist/es6.js';
import spartaMessageUtility from '@sparta.global.utilities/sparta-messaging-utility/dist/es6.js';
import renderModuleTemplate from '@sparta.global.templates/render-module-template/dist/es6.js';

class Communicator {
  constructor(options) {
    let defaults = {
      public: "document",
      $public: $(),
      private: "",
      $private: $(),
      namespace: "generic"
    };
    this.settings = {};
    this.settings.events = defaults;
    $.extend(this.settings, options);
    this.communication = this.settings.events;
    this.communication.$private = this.settings.$target;
    this.communication.$public = $(this.communication.public);
  }

  // targeting the following code for deprecation.
  // After August release we will delete beacon API from sparta-module-utility
  beacon(action, params) {
    // Wiring up the beacon to work temporarily.
    // Will rewire to use the SpartaEvent pattern.
    //
    let utilities = {
      array: {
        slice: function (array) {
          return Array.prototype.slice.apply(array, Array.prototype.slice.call(arguments, 1));
        },
        remove: function (array, item) {
          var i,
            l = array.length;
          for (i = 0; i < l; i++) {
            if (array[i] === item) {
              array.splice(i, 1);
              i--;
            }
          }
        }
      },
      functional: function (f) {
        var slice = utilities.array.slice,
          prepended = [],
          appended = [],
          boundTo = null,
          before,
          after;
        function wrapper() {
          if (before != null) {
            before.apply(null, arguments);
          }
          var result = f.apply(boundTo, prepended.concat(slice(arguments), appended));
          if (after != null) {
            after.call(null, result);
          }
          return result;
        }
        wrapper.bindTo = function (to) {
          boundTo = to;
          return wrapper;
        };
        wrapper.append = function () {
          appended = slice(arguments);
          return wrapper;
        };
        wrapper.prepend = function () {
          prepended = slice(arguments);
          return wrapper;
        };
        wrapper.before = function (f) {
          before = f;
          return wrapper;
        };
        wrapper.after = function (f) {
          after = f;
          return wrapper;
        };
        return wrapper;
      }
    };
    if (typeof params.context !== "string") return;
    let $location = params.scope === "public" ? this.communication.$public : this.communication.$private;
    if (action === "listen" && typeof params.callback === "function") {
      $location.on(params.context + "." + this.communication.namespace, utilities.functional(params.callback).bindTo(params.caller));
    }
    if (action === "emit") {
      $location.trigger(params.context + "." + this.communication.namespace, [params.data]);
    }
  }
  listen(params) {
    this.beacon("listen", params);
  }
  emit(params) {
    this.beacon("emit", params);
  }
}

class Controller extends Communicator {
  constructor(options) {
    super(options.settings);
    let defaults = {};
    this.settings = defaults;
    $.extend(this.settings, options.settings);
    this.model = options.model;
  }
}

class Model extends Communicator {
  constructor(options) {
    super(options.settings);
    var defaults = {};
    this.settings = defaults;
    $.extend(this.settings, options.settings);
    this.protected = [];
    this._initialize();
  }
  get data() {
    return this.protected;
  }
  set data(value) {
    this.protected = value;
  }
  _initialize() {
    // Model initialization code here.
  }
}

class View extends Communicator {
  constructor(options) {
    super(options.settings);
    let defaults = {};
    this.settings = defaults;
    this.settings = $.extend(defaults, options.settings);
    this.model = options.model;
    this._initialize();
  }
  _initialize() {
    // View initialization code here.
  }
}

// Third party imports
function registerModuleClass({
  moduleClass,
  moduleConfig
}) {
  spartaBootstrapUtility.registerModuleClass({
    moduleClass,
    moduleConfig
  });
}

/**
 * Validate and retrieve the target element
 * @param {string|HTMLElement} target Target element
 * @returns {HTMLElement} Validated target element
 */
function getMountElement(target) {
  const element = target instanceof HTMLElement ? target : document.querySelector(target);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Target ${target} isn't a valid HTML element or query selector string.`);
  }
  return element;
}

/**
 * Verify and create a new module Id if required
 * @param {string} module Id
 * @returns {string} same or new module Id
 */
function getModuleUniqueId(moduleId) {
  if (!document.getElementById(moduleId)) {
    return moduleId;
  }
  let moduleCount = 0;
  while (document.getElementById(moduleId + ++moduleCount));
  return moduleId + moduleCount;
}
class Module {
  constructor(options) {
    if (options && Object.keys(options).length) {
      const defaults = {
        target: null,
        $target: $(),
        debug: false,
        events: {
          namespace: "module"
        }
      };
      this.settings = {
        ...defaults,
        ...options
      };
      this.settings.target = this.settings.class || this.settings.target;
      this.settings.$target = $(this.settings.target); // How can we get rid of $target? If we dont then we will never be able to get rid of jQuery
      this.settings.SPARTA_READY = this.settings.$target.data("sparta-load") === "primary" ? "SPARTA_GLOBAL_READY.spartaBootstrapUtility" : "SPARTA_SECONDARY_READY.spartaBootstrapUtility";

      /*  Gather all appropriate data attributes associated with this module.
          These can then be accessed as overrides on default actions/behaviors/values.
      */
      for (const data in this.settings.data) {
        if (this.settings.$target.data(data)) {
          this.settings.data[data] = this.settings.$target.data(data);
        }
      }
      this.model = new Model({
        settings: this.settings
      });
      this.view = new View({
        model: this.model,
        settings: this.settings
      });
      this.controller = new Controller({
        model: this.model,
        settings: this.settings
      });
      this.Events = spartaEventsUtility;
      this.Store = spartaStoreUtility;
      this.Transport = spartaTransportUtility;
      this.SpartaFunctions = spartaGlobalFunctions;
      this.Experience = spartaExperienceUtility;
      this.Message = spartaMessageUtility;
      this.isWidget = !!spartaStoreUtility.getData("isWidget");
      if (this.isWidget) {
        this.widgetInfo = options.widgetInfo;
      }
      this.applicationContext = spartaStoreUtility.getData("applicationContext", this.widgetInfo);
      this.applicationContainer = spartaStoreUtility.getData("applicationContainer", this.widgetInfo);
      this.spaParams = spartaStoreUtility.getData("spaParams");
      this.eventDelegatorString = this.applicationContext ? `#${this.applicationContext.id}` : "[data-sparta-container]";
      this.params = this.settings.$target.data("module-parameters");
      this.vuex = options.vuex;
      this.vuexStore = options.vuexStore;

      // sets module info (name/version/options)
      this.settings.moduleInfo = {
        name: this.settings.$target.data("module"),
        version: this.settings.$target.data("version")
      };

      // options are set on applicationContainer in 'data-sparta-overrides' via Bootstrap-utility
      this.settings.moduleInfo.options = this.Store.getData("applicationOptions", this.widgetInfo)[this.settings.moduleInfo.name];
      spartaBootstrapUtility.register(this);
    }
  }

  /**
   * Loads on-demand data sources asynchronously and replaces data source reference with loaded data object
   */

  async loadDataSourceAsync(jcrReference) {
    // Skip download if jcrReference is already an object
    if (typeof jcrReference === "string") {
      // const onDemandJcrLoader = window.dynamicImportMap.jcrs.onDemand[jcrReference]
      const spaParams = spartaStoreUtility.getData("spaParams");
      const dynamicImport = spaParams["dynamicImportMap"];
      let jcrEntry;
      if (dynamicImport) {
        jcrEntry = dynamicImport.jcrs.find(a => a.name === jcrReference);
      }
      if (jcrEntry === undefined) {
        throw new Error(`ON-DEMAND JCR NOT FOUND: ${jcrReference}`);
      }
      const loadedData = await jcrEntry.loader();
      // dataSources[dataSourceName] = loadedData.default
      return loadedData.default;
    }
    if (typeof jcrReference === "object") {
      // Return the jcrReference as is since it has already an object
      return jcrReference;
    }
    throw new Error(`ON-DEMAND JCR REFERENCE UNKNOWN: ${jcrReference}`);
  }

  /**
   * Mounts a set of module objects (or single object) to the given selectors/elements.
   *
   * Schema:
   * (required) module: reference of the module to mount
   * (required) target: string (or HTMLElement) of the element to mount the module to
   * (optional) context: object of dataSources and/or params to compile the module with.
   * dataSource values can either be relative-pathed URLs or objects, and any URLs provided will be
   * fully resolved as objects.
   * params values must be fully resolved.
   *
   * Advanced:
   * (optional) version: hardcoded version of the module to load, such as '1.0.0'.
   *
   *
   * Usage #1: module is an Array of modules to mount
   * this.mountAll([{
   *   module: 'sparta-demo/global-nav-module',
   *   target: '#some-selector',
   *   context: {
   *     dataSources: {
   *       data1: '/sparta-demo/some/path/file.json'
   *     },
   *     params: {
   *       foo: 'bar'
   *     }
   *   }
   * }]);
   *
   * Usage #2: module is a single object that represents a single module to mount
   * this.mountAll({
   *   module: 'sparta-demo/global-nav-module',
   *   target: '#some-selector',
   *   context: {
   *     dataSources: {
   *       data1: '/sparta-demo/some/path/file.json'
   *     },
   *     params: {
   *       foo: 'bar'
   *     }
   *   }
   * });
   *
   * @param {Array|Object} module An array (or single object) of modules to load.
   * @memberof Module
   * @returns {Promise}
   */

  async mountAll(modules) {
    const moduleArray = Array.isArray(modules) ? modules : [modules];
    const spaParams = spartaStoreUtility.getData("spaParams");
    const dynamicImport = spaParams["dynamicImportMap"];
    const widgetInfo = this.widgetInfo || [];
    for (const moduleDef of moduleArray) {
      const {
        module,
        target,
        context,
        childModules
      } = moduleDef;
      let moduleEntry;
      if (dynamicImport) {
        moduleEntry = dynamicImport.modules.find(a => a.name === module);
      }
      if (moduleEntry === undefined) {
        throw new Error(`ERROR - ON-DEMAND MODULE NOT FOUND: ${module}`);
      }

      // Execute dynamic import
      const [loadedOnDemandModuleData] = await moduleEntry.loader();
      // Verify component loaded successfully
      if (!loadedOnDemandModuleData) {
        throw new Error(`ERROR - ON-DEMAND MODULE FAILED TO LOAD: ${module}`);
      }
      const {
        moduleConfig
      } = loadedOnDemandModuleData;

      // Process child modules
      if (childModules && childModules.length) {
        for (const childModule of childModules) {
          const childModuleName = childModule.module;
          const childModuleContext = childModule.context;
          const childModuleEntry = dynamicImport.modules.find(a => a.name === childModuleName);
          if (childModuleEntry === undefined) {
            throw new Error(`ERROR - ON-DEMAND MODULE'S CHILD MODULE NOT FOUND: Module - ${module}, CHILD MODULE - ${childModuleName}`);
          }
          const [childModuleData] = await childModuleEntry.loader();
          if (!childModuleData) {
            throw new Error(`ERROR - ON-DEMAND MODULE'S CHILD MODULE FAILED TO LOAD:  Module - ${module}, CHILD MODULE - ${childModuleName}`);
          }
          const childModuleConfig = childModuleData.moduleConfig;
          // Creare child module render context
          Object.assign(childModule, await this.createModuleRenderContext(childModuleContext, childModuleConfig));

          // Register Child module class
          spartaBootstrapUtility.registerModuleClass({
            moduleClass: childModuleData.default,
            moduleConfig: childModuleConfig
          });
        }
      }

      // Create parent module render context
      const moduleRenderContext = await this.createModuleRenderContext(context, moduleConfig, childModules);
      const moduleRenderData = {
        env: this.spaParams.siteParams.env,
        site: {
          global: this.spaParams.global,
          params: this.spaParams.siteParams
        },
        page: {
          parameters: this.spaParams.pageParams
        },
        language: this.spaParams.language,
        assetPath: "",
        module: moduleRenderContext,
        partials: moduleConfig.partials,
        helpers: moduleConfig.helpers
      };
      const html = renderModuleTemplate.render(moduleRenderData);

      // Inject the rendered HTML into the DOM
      const element = getMountElement(target);
      element.innerHTML += html;
      spartaBootstrapUtility.addPreventDefaultForLinks(element);
      const moduleClass = loadedOnDemandModuleData.default;
      spartaBootstrapUtility.registerModuleClass({
        moduleClass,
        moduleConfig
      });
    }
    await spartaBootstrapUtility.createAndInitModules(widgetInfo);
  }
  async resolveModulesDataSources(dataSourcesMap) {
    dataSourcesMap = dataSourcesMap || {};
    const dataSourceKeys = Object.keys(dataSourcesMap);
    const dataSourcePromiseArray = [];
    dataSourceKeys.forEach(key => {
      if (!this.spaParams) this.spaParams = spartaStoreUtility.getData("spaParams");
      const {
        language
      } = this.spaParams;
      const jcrReference = dataSourcesMap[key][language] || dataSourcesMap[key];
      dataSourcePromiseArray.push(this.loadDataSourceAsync(jcrReference));
    });

    // Wait for all the data sources to load
    const dataSourceResults = await Promise.all(dataSourcePromiseArray);
    dataSourceKeys.forEach((key, index) => {
      dataSourcesMap[key] = dataSourceResults[index];
    });
  }
  async createModuleRenderContext(context, moduleConfig, childModules = null) {
    await this.resolveModulesDataSources(context.dataSources);
    const uniqueId = getModuleUniqueId(moduleConfig.id);
    const moduleRenderContext = {
      config: {
        ...moduleConfig,
        id: uniqueId
      },
      params: {
        ...context.params
      },
      ...context.params,
      dataSources: context.dataSources,
      childModules
    };

    // Populate params with default and overridden entries
    moduleConfig.moduleParams.map(param => {
      moduleRenderContext.params[param.name] = moduleRenderContext.params[param.name] !== undefined ? moduleRenderContext.params[param.name] : param.default ? param.default : "";
    });

    // Populate params with selected module config properties
    const paramsConfigProperties = ["moduleName", "version", "moduleParams", "viewPort", "id", "nameSpace", "tag", "components", "semantic"];
    paramsConfigProperties.map(propertyName => {
      if (moduleConfig[propertyName]) {
        moduleRenderContext.params[propertyName] = moduleConfig[propertyName];
      }
    });
    return moduleRenderContext;
  }
}

export { Module as default, getModuleUniqueId, getMountElement, registerModuleClass };
